import { memo, useMemo, useState, useCallback } from 'react';

import { isMobile } from 'hooks';
import { List } from 'components/list';
import { formatDate } from 'utils/date';
import { Payout } from 'store/api/payout';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { Drawer, DrawerHeader } from 'components/drawer';

import { BonusesDrawerProps } from './types';
import { BonusDetailsDrawer } from './details';

const template = 'repeat(2, 80px) repeat(3, 1fr)';
const titles = ['Date', 'Amount', 'Type', 'Employee', 'Notes'];

const BonusCard = memo(
  ({ value, onSelect }: { value: Payout; onSelect: (id: number) => void }) => {
    const handleClick = useCallback(
      () => onSelect(value.id),
      [value.id, onSelect]
    );

    return (
      <ListCard
        key={value?.id}
        headers={titles}
        template={template}
        onClick={handleClick}
        values={[
          formatDate(value?.date),
          formatPrice(value?.amount),
          value?.type,
          value?.employee?.user?.name,
          value?.notes,
        ]}
      />
    );
  }
);

export const BonusesDrawer = memo(
  ({ open, onClose, bonuses }: BonusesDrawerProps) => {
    const mobile = isMobile();
    const [selectedBonusId, setSelectedBonusId] = useState<number>();

    const handleBonusSelect = useCallback((id: number) => {
      setSelectedBonusId(id);
    }, []);

    const bonusList = useMemo(
      () =>
        bonuses?.map((value) => (
          <BonusCard
            value={value}
            key={value.id}
            onSelect={handleBonusSelect}
          />
        )),
      [bonuses, handleBonusSelect]
    );

    if (!open) {
      return null;
    }

    return (
      <Drawer hideOverlay opened={true} topOffset={mobile ? '10%' : '15%'}>
        <DrawerHeader onClose={onClose} title="Bonuses Details" />
        {!selectedBonusId && (
          <List
            count={bonuses?.length}
            mainWrapperStyles={{ padding: 0 }}
            header={<ListHeader titles={titles} template={template} />}
          >
            {bonusList}
          </List>
        )}

        {selectedBonusId && (
          <BonusDetailsDrawer
            id={selectedBonusId}
            onClose={() => setSelectedBonusId(undefined)}
          />
        )}
      </Drawer>
    );
  }
);
